import eventBus from "./eventBus";

/**
 * Single place that describes how each notification type is shown.
 *
 * Entry shape: { title, body(data), actions?(data, ctx) }
 * `actions` are kept for the upcoming CTA work but are not rendered yet.
 * To change the wording of a notification, edit its entry here.
 */

const SOURCE_SUFFIX = {
    public: ' via the public link',
    invite: ' via your invite',
    invite_code: ' with an invite code',
};

const REACTION_VERB = {
    like: 'liked',
    love: 'loved',
    laugh: 'laughed at',
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const inClub = (d) => (d.club_title ? ` in ${d.club_title}` : '');
const club = (d, fallback = 'your club') => d.club_title || fallback;
const quoted = (text) => (text ? `"${text}"` : '');

const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(`${isoDate}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

const fallbackBody = (d) => d.message ?? d.body ?? d.excerpt ?? '';

const humanize = (type) =>
    type ? type.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Notification';

const REGISTRY = {
    // --- Discussions ---
    comment_reply: {
        title: 'New reply',
        body: (d) => (d.replier_name
            ? `${d.replier_name} replied to your comment${d.excerpt ? `: ${quoted(d.excerpt)}` : ''}`
            : fallbackBody(d)),
    },
    comment_reaction: {
        title: 'New reaction',
        body: (d) => (d.reactor_name
            ? `${d.reactor_name} ${REACTION_VERB[d.reaction_type] ?? 'reacted to'} your comment${d.excerpt ? `: ${quoted(d.excerpt)}` : ''}`
            : fallbackBody(d)),
    },
    user_mentioned: {
        title: 'You were mentioned',
        body: (d) => (d.mentioner_name
            ? `${d.mentioner_name} mentioned you${d.excerpt ? `: ${quoted(d.excerpt)}` : ''}`
            : fallbackBody(d)),
    },
    new_discussion: {
        title: 'New discussion',
        body: (d) => (d.title
            ? `${d.author_name ? `${d.author_name} started` : 'New discussion'} ${quoted(d.title)}${inClub(d)}`
            : fallbackBody(d)),
    },

    // --- Club activity ---
    new_episode_released: {
        title: 'New episode released',
        body: (d) => (d.episode_title
            ? `${quoted(d.episode_title)} is now available${inClub(d)}`
            : `A new episode is now available${inClub(d)}`),
    },
    first_engagement_nudge: {
        title: 'Join the conversation',
        body: (d) => `You haven't joined the conversation${inClub(d)} yet. Share a thought or try a practice to get started.`,
    },
    podcaster_private_reply: {
        title: 'New private reply',
        body: (d) => `${d.host_name || 'Your host'} replied to your private message${inClub(d)}`,
    },
    new_follower: {
        title: 'New follower',
        body: (d) => `${d.listener_name || 'Someone'} started following you`,
    },
    new_member_joined: {
        title: 'New member joined',
        body: (d) => `${d.listener_name || 'Someone'} joined ${club(d)}${SOURCE_SUFFIX[d.source] ?? ''}`,
        actions: (d) => {
            if (!d.user_guid) return [];
            return [{
                key: 'view-profile',
                label: 'View profile',
                run: () => eventBus.emit('userinfo:open', {
                    userGuid: d.user_guid,
                    clubGuid: d.club_guid,
                    context: { type: 'club', data: {} },
                }),
            }];
        },
    },

    // --- Digests ---
    listener_digest: {
        title: 'Your club digest',
        body: (d) => {
            if (d.is_fallback) return `There has been new activity${inClub(d)} while you were away.`;
            const parts = [];
            if (d.reply_count) parts.push(plural(d.reply_count, 'reply', 'replies'));
            if (d.mention_count) parts.push(plural(d.mention_count, 'mention', 'mentions'));
            return parts.length
                ? `${parts.join(' and ')} since your last digest${inClub(d)}`
                : `New activity${inClub(d)} since your last digest`;
        },
    },
    podcaster_digest: {
        title: 'Your club digest',
        body: (d) => {
            const parts = [];
            if (d.postCount) parts.push(plural(d.postCount, 'new post', 'new posts'));
            if (d.practiceCount) parts.push(plural(d.practiceCount, 'practice', 'practices'));
            const joined = d.joinNames?.length ?? 0;
            if (joined) parts.push(plural(joined, 'new member', 'new members'));
            return parts.length
                ? `${parts.join(', ')}${inClub(d)} since your last digest`
                : `New activity${inClub(d)} since your last digest`;
        },
    },

    // --- Club closing: listener ---
    club_close_email_1: {
        title: 'Club closing soon',
        body: (d) => {
            const date = formatDate(d.closes_at);
            return `${club(d, 'Your club')} closes${date ? ` on ${date}` : ' soon'}. Make the most of the time you have left.`;
        },
    },
    club_close_email_2: {
        title: 'Club closes today',
        body: (d) => `${club(d, 'Your club')} closes today. This is your last chance to join in.`,
    },
    club_close_email_3: {
        title: 'Club has closed',
        body: (d) => `${club(d, 'Your club')} has wrapped up. Thanks for being part of it.`,
    },

    // --- Club closing: podcaster ---
    podcaster_club_close_email_1: {
        title: 'Your club closes soon',
        body: (d) => {
            const stats = [];
            if (d.activeMemberCount) stats.push(plural(d.activeMemberCount, 'active member', 'active members'));
            if (d.openConversationsCount) stats.push(plural(d.openConversationsCount, 'open conversation', 'open conversations'));
            return `${club(d, 'Your club')} closes in 7 days${stats.length ? `, with ${stats.join(' and ')}` : ''}.`;
        },
    },
    podcaster_club_close_email_2: {
        title: 'Your club has closed',
        body: (d) => {
            const stats = [];
            if (d.joinedCount) stats.push(plural(d.joinedCount, 'member', 'members') + ' joined');
            if (d.activeMemberCount) stats.push(`${d.activeMemberCount} ${d.activeMemberCount === 1 ? 'was' : 'were'} active`);
            return `${club(d, 'Your club')} has wrapped up${stats.length ? `: ${stats.join(', ')}` : ''}.`;
        },
    },
};

export const getNotificationTitle = (data) => REGISTRY[data?.type]?.title ?? humanize(data?.type);

export const getNotificationBody = (data) => {
    if (!data) return '';
    const entry = REGISTRY[data.type];
    // `message` is set by backend for ad-hoc/test notifications and always wins when present
    if (data.message) return data.message;
    return entry ? entry.body(data) : fallbackBody(data);
};

export const getNotificationActions = (data, ctx = {}) => {
    if (!data) return [];
    return REGISTRY[data.type]?.actions?.(data, ctx) ?? [];
};
